/**
 * Pure helpers for the Lab's layout: which scope and tab the right pane shows
 * (from the address), which tabs it offers, and the runs rail's remembered
 * collapsed state. Nothing here touches the router or the DOM.
 */
import {
  DEFAULT_LAB_RUN_TAB,
  DEFAULT_LAB_TURN_TAB,
  LAB_RUN_TABS,
  LAB_RUN_TAB_HELP,
  LAB_RUN_TAB_LABELS,
  LAB_TURN_TABS,
  LAB_TURN_TAB_HELP,
  LAB_TURN_TAB_LABELS,
  type LabPaneId,
  type LabSelection,
  type LabTabId,
} from "./labUrl";

/** The scope in use: the address's, else "This turn" once a turn is selected. */
export function effectivePane(selection: LabSelection): LabPaneId {
  return selection.pane ?? (selection.turn !== null ? "turn" : "run");
}

/** The tabs a pane offers; "Compare" is a Whole run tab only while compare mode is on. */
export function paneTabs(
  pane: LabPaneId,
  compareOn: boolean,
): readonly { id: LabTabId; label: string; help: string }[] {
  if (pane === "turn") {
    return LAB_TURN_TABS.map((id) => ({
      id,
      label: LAB_TURN_TAB_LABELS[id],
      help: LAB_TURN_TAB_HELP[id],
    }));
  }
  return LAB_RUN_TABS.filter((id) => compareOn || id !== "compare").map(
    (id) => ({
      id,
      label: LAB_RUN_TAB_LABELS[id],
      help: LAB_RUN_TAB_HELP[id],
    }),
  );
}

/** The selected tab of a pane: the address's when that pane offers it, else the pane's default. */
export function effectiveTab(
  pane: LabPaneId,
  tab: LabTabId | null,
  compareOn: boolean,
): LabTabId {
  if (tab !== null && paneTabs(pane, compareOn).some((t) => t.id === tab)) {
    return tab;
  }
  return pane === "turn" ? DEFAULT_LAB_TURN_TAB : DEFAULT_LAB_RUN_TAB;
}

/** `relative` clips the pane's `sr-only` live regions with it; without it one at the end of a long pane extends the page. */
export const LAB_SCROLL_PANE = "relative min-h-0 overflow-y-auto";

export const LAB_RUNS_COLLAPSED_KEY = "lab.runsCollapsed";

/** Remembered collapsed state of the runs pane; collapsed until the clinician opens it. */
export function readRunsCollapsed(): boolean {
  try {
    return window.localStorage.getItem(LAB_RUNS_COLLAPSED_KEY) !== "0";
  } catch {
    return true;
  }
}

export function writeRunsCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(LAB_RUNS_COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch {
    // Storage blocked or full: the choice simply is not remembered.
  }
}
